import React, { useEffect, useState, Component } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FileTextIcon, AwardIcon, PlusIcon, TrashIcon, TruckIcon, PackageIcon } from 'lucide-react';
import { FormSection, TextField, TextAreaField, DisplayField, FileUploadField } from '../components/FormComponents';
import { mockPOs, mockPRs, mockSuppliers } from '../data/mockData';
// Mock NOA awards: maps PR number to the awarded supplier from NOA
const mockNOAAwards: Record<string, {
  supplierName: string;
  supplierCode: string;
  noaRef: string;
  awardedAmount: number;
}> = {
  'PR-2023-001': {
    supplierName: 'TechCorp Solutions',
    supplierCode: 'SUP-001',
    noaRef: 'NOA-2023-001',
    awardedAmount: 6000
  },
  'PR-2023-002': {
    supplierName: 'Office Supplies Co.',
    supplierCode: 'SUP-002',
    noaRef: 'NOA-2023-002',
    awardedAmount: 15000
  }
};
export function PurchaseOrderForm() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const mode = id ? 'edit' : 'create';
  const initialData = mode === 'edit' && id ? mockPOs.find(p => p.id === id) : null;
  const [formData, setFormData] = useState({
    poNo: initialData?.poNo || 'PO-AUTO-GEN',
    linkedPRNo: initialData?.linkedPRNo || '',
    vendor: initialData?.vendor || '',
    department: initialData?.department || '',
    project: initialData?.project || '',
    procurementMethod: initialData?.procurementMethod || '',
    poDate: initialData?.poDate || new Date().toISOString().split('T')[0],
    deliveryDate: initialData?.deliveryDate || '',
    termsConditions: initialData?.termsConditions || '',
    remarks: initialData?.remarks || ''
  });
  const [lineItems, setLineItems] = useState<any[]>(initialData?.lineItems || []);
  const [totalAmount, setTotalAmount] = useState(initialData?.totalAmount || 0);
  const [noaInfo, setNoaInfo] = useState<{
    noaRef: string;
    supplierCode: string;
  } | null>(initialData?.linkedPRNo ? mockNOAAwards[initialData.linkedPRNo] ? {
    noaRef: mockNOAAwards[initialData.linkedPRNo].noaRef,
    supplierCode: mockNOAAwards[initialData.linkedPRNo].supplierCode
  } : null : null);
  // Phase-wise delivery state
  interface PhaseItemAllocation {
    itemId: string;
    qty: number;
  }
  interface DeliveryPhase {
    id: string;
    description: string;
    deliveryDate: string;
    paymentPercent: number;
    lineItemAllocations: PhaseItemAllocation[];
  }
  const [deliveryPhases, setDeliveryPhases] = useState<DeliveryPhase[]>([{
    id: '1',
    description: '',
    deliveryDate: '',
    paymentPercent: 100,
    lineItemAllocations: []
  }]);
  const [expandedPhase, setExpandedPhase] = useState<string | null>('1');
  const totalPaymentPercent = deliveryPhases.reduce((sum, phase) => sum + phase.paymentPercent, 0);
  const handleAddPhase = () => {
    const newId = Date.now().toString();
    setDeliveryPhases([...deliveryPhases, {
      id: newId,
      description: '',
      deliveryDate: '',
      paymentPercent: 0,
      lineItemAllocations: []
    }]);
    setExpandedPhase(newId);
  };
  const handleRemovePhase = (phaseId: string) => {
    if (deliveryPhases.length > 1) {
      setDeliveryPhases(deliveryPhases.filter(p => p.id !== phaseId));
    }
  };
  const handleUpdatePhase = (phaseId: string, field: keyof DeliveryPhase, value: any) => {
    setDeliveryPhases(deliveryPhases.map(p => p.id === phaseId ? {
      ...p,
      [field]: value
    } : p));
  };
  const handleUpdateItemAllocation = (phaseId: string, itemId: string, qty: number) => {
    setDeliveryPhases(deliveryPhases.map(p => {
      if (p.id !== phaseId) return p;
      const existing = p.lineItemAllocations.find(a => a.itemId === itemId);
      if (qty <= 0) {
        return {
          ...p,
          lineItemAllocations: p.lineItemAllocations.filter(a => a.itemId !== itemId)
        };
      }
      if (existing) {
        return {
          ...p,
          lineItemAllocations: p.lineItemAllocations.map(a => a.itemId === itemId ? {
            ...a,
            qty
          } : a)
        };
      }
      return {
        ...p,
        lineItemAllocations: [...p.lineItemAllocations, {
          itemId,
          qty
        }]
      };
    }));
  };
  const getAllocatedQty = (itemId: string, excludePhaseId?: string) => {
    return deliveryPhases.filter(p => p.id !== excludePhaseId).reduce((sum, p) => {
      const alloc = p.lineItemAllocations.find(a => a.itemId === itemId);
      return sum + (alloc?.qty || 0);
    }, 0);
  };
  // Only show Approved PRs for selection
  const approvedPRs = mockPRs.filter(pr => pr.status === 'Approved');
  // When PR is selected, auto-populate ALL fields including supplier from NOA
  const handlePRSelect = (prNo: string) => {
    const selectedPR = mockPRs.find(pr => pr.prNo === prNo);
    const noaAward = prNo ? mockNOAAwards[prNo] : null;
    if (selectedPR) {
      setFormData(prev => ({
        ...prev,
        linkedPRNo: prNo,
        department: selectedPR.departmentCode,
        project: selectedPR.project,
        procurementMethod: selectedPR.procurementMethod,
        deliveryDate: selectedPR.estimatedDeliveryDate,
        vendor: noaAward?.supplierName || ''
      }));
      setLineItems(selectedPR.lineItems);
      setTotalAmount(selectedPR.total);
      setNoaInfo(noaAward ? {
        noaRef: noaAward.noaRef,
        supplierCode: noaAward.supplierCode
      } : null);
    } else {
      setFormData(prev => ({
        ...prev,
        linkedPRNo: '',
        department: '',
        project: '',
        procurementMethod: '',
        vendor: ''
      }));
      setLineItems([]);
      setTotalAmount(0);
      setNoaInfo(null);
    }
  };
  const hasPRSelected = formData.linkedPRNo !== '';
  return <div className="space-y-6 max-w-6xl mx-auto" data-id="element-1472">
      <div className="flex items-center justify-between" data-id="element-1473">
        <div data-id="element-1474">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1475">
            {mode === 'create' ? 'Create Purchase Order' : 'Edit Purchase Order'}
          </h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-1476">
            {mode === 'create' ? 'Select an approved Purchase Requisition to generate a Purchase Order.' : 'Update the purchase order details.'}
          </p>
        </div>
      </div>

      <form className="bg-white shadow-sm rounded-lg border border-gray-200 p-6 md:p-8" data-id="element-1477">
        <FormSection title="Basic Information" data-id="element-1478">
          <DisplayField label="PO No" value={formData.poNo} data-id="element-1479" />

          {/* Linked PR Selection */}
          {mode === 'edit' ? <DisplayField label="Linked PR No" value={formData.linkedPRNo} data-id="element-1480" /> : <div data-id="element-1481">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1482">
                Linked PR No <span className="text-red-500" data-id="element-1483">*</span>
              </label>
              <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2 bg-white" value={formData.linkedPRNo} onChange={e => handlePRSelect(e.target.value)} data-id="element-1484">
                <option value="" data-id="element-1485">Select Approved PR...</option>
                {approvedPRs.map(pr => <option key={pr.id} value={pr.prNo} data-id="element-1486">
                    {pr.prNo} — {pr.requisitionFor} ({pr.procurementMethod}) — $
                    {pr.total.toLocaleString()}
                  </option>)}
              </select>
              {approvedPRs.length === 0 && <p className="mt-1 text-xs text-amber-600" data-id="element-1487">
                  No approved Purchase Requisitions available.
                </p>}
            </div>}

          {/* Supplier — Display Only from NOA */}
          <div data-id="element-1488">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1489">
              Supplier
            </label>
            {formData.vendor ? <div className="bg-green-50 border border-green-200 rounded-md p-3" data-id="element-1490">
                <div className="flex items-center justify-between" data-id="element-1491">
                  <div className="flex items-center gap-2" data-id="element-1492">
                    <AwardIcon className="h-4 w-4 text-green-600" data-id="element-1493" />
                    <span className="text-sm font-semibold text-gray-900" data-id="element-1494">
                      {formData.vendor}
                    </span>
                    {noaInfo && <span className="text-xs text-gray-500" data-id="element-1495">
                        ({noaInfo.supplierCode})
                      </span>}
                  </div>
                  {noaInfo && <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-1496">
                      NOA: {noaInfo.noaRef}
                    </span>}
                </div>
                <p className="mt-1 text-xs text-green-700" data-id="element-1497">
                  Awarded via Notice of Award — supplier cannot be changed.
                </p>
              </div> : <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-sm text-gray-500" data-id="element-1498">
                {hasPRSelected ? 'No NOA found for this PR. Please complete the procurement process first.' : 'Select a Purchase Requisition to view the awarded supplier.'}
              </div>}
          </div>

          {/* Display-only fields populated from PR */}
          <DisplayField label="Department" value={formData.department || '—'} data-id="element-1499" />
          <DisplayField label="Project" value={formData.project || '—'} data-id="element-1500" />
          <DisplayField label="Procurement Method" value={formData.procurementMethod || '—'} data-id="element-1501" />

          <TextField label="PO Date" name="poDate" type="date" value={formData.poDate} onChange={e => setFormData({
          ...formData,
          poDate: e.target.value
        })} required data-id="element-1502" />
        </FormSection>

        {/* Line Items — Display Only from PR */}
        <div className="mb-8" data-id="element-1503">
          <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-200" data-id="element-1504">
            <div className="flex items-center gap-2" data-id="element-1505">
              <FileTextIcon className="h-5 w-5 text-gray-400" data-id="element-1506" />
              <h3 className="text-lg font-medium text-gray-900" data-id="element-1507">Line Items</h3>
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full" data-id="element-1508">
                Populated from PR
              </span>
            </div>
          </div>

          {lineItems.length > 0 ? <div className="overflow-x-auto" data-id="element-1509">
              <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-md" data-id="element-1510">
                <thead className="bg-gray-50" data-id="element-1511">
                  <tr data-id="element-1512">
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1513">
                      Product / Asset
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-24" data-id="element-1514">
                      Qty
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-32" data-id="element-1515">
                      Rate
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-32" data-id="element-1516">
                      Est. Cost
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1517">
                      Specification
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-1518">
                  {lineItems.map((item: any) => <tr key={item.id} data-id="element-1519">
                      <td className="px-4 py-3 text-sm text-gray-900 font-medium" data-id="element-1520">
                        {item.product}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700" data-id="element-1521">
                        {item.qty}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700" data-id="element-1522">
                        ${item.rate.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 bg-gray-50" data-id="element-1523">
                        ${item.estimatedCost.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500" data-id="element-1524">
                        {item.specification || '—'}
                      </td>
                    </tr>)}
                </tbody>
                <tfoot className="bg-gray-50" data-id="element-1525">
                  <tr data-id="element-1526">
                    <td colSpan={3} className="px-4 py-3 text-right text-sm font-medium text-gray-700" data-id="element-1527">
                      Total Amount:
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-indigo-600" data-id="element-1528">
                      ${totalAmount.toLocaleString()}
                    </td>
                    <td data-id="element-1529"></td>
                  </tr>
                </tfoot>
              </table>
            </div> : <div className="text-center py-8 border border-dashed border-gray-300 rounded-md bg-gray-50" data-id="element-1530">
              <FileTextIcon className="mx-auto h-8 w-8 text-gray-300" data-id="element-1531" />
              <p className="mt-2 text-sm text-gray-500" data-id="element-1532">
                {hasPRSelected ? 'No line items found in the selected PR.' : 'Select a Purchase Requisition to view line items.'}
              </p>
            </div>}
        </div>

        {/* Phase-wise Delivery Schedule */}
        <div className="mb-8" data-id="element-1533">
          <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-200" data-id="element-1534">
            <div className="flex items-center gap-2" data-id="element-1535">
              <TruckIcon className="h-5 w-5 text-gray-400" data-id="element-1536" />
              <h3 className="text-lg font-medium text-gray-900" data-id="element-1537">
                Phase-wise Delivery Schedule
              </h3>
            </div>
            <button type="button" onClick={handleAddPhase} className="inline-flex items-center px-3 py-1.5 border border-indigo-200 text-sm font-medium rounded-md text-indigo-700 bg-white hover:bg-indigo-50 transition-colors" data-id="element-1538">
              <PlusIcon className="w-4 h-4 mr-1" data-id="element-1539" /> Add Phase
            </button>
          </div>

          <div className="space-y-3" data-id="element-1540">
            {deliveryPhases.map((phase, idx) => {
            const isExpanded = expandedPhase === phase.id;
            const phaseAmount = totalAmount * phase.paymentPercent / 100;
            const allocatedItemCount = phase.lineItemAllocations.filter(a => a.qty > 0).length;
            return <div key={phase.id} className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-1541">
                  {/* Phase header row */}
                  <div className="bg-gray-50 px-4 py-3 flex items-center gap-4" data-id="element-1542">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 text-sm font-bold flex-shrink-0" data-id="element-1543">
                      {idx + 1}
                    </span>
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-3" data-id="element-1544">
                      <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm border p-1.5" value={phase.description} onChange={e => handleUpdatePhase(phase.id, 'description', e.target.value)} placeholder={`Phase ${idx + 1} description...`} data-id="element-1545" />
                      <input type="date" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm border p-1.5" value={phase.deliveryDate} onChange={e => handleUpdatePhase(phase.id, 'deliveryDate', e.target.value)} data-id="element-1546" />
                      <div className="flex items-center" data-id="element-1547">
                        <input type="number" min="0" max="100" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm border p-1.5 text-right" value={phase.paymentPercent} onChange={e => handleUpdatePhase(phase.id, 'paymentPercent', Number(e.target.value))} data-id="element-1548" />
                        <span className="ml-1 text-sm text-gray-500 whitespace-nowrap" data-id="element-1549">
                          % = $
                          {phaseAmount.toLocaleString(undefined, {
                        maximumFractionDigits: 0
                      })}
                        </span>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-1550">
                        <button type="button" onClick={() => setExpandedPhase(isExpanded ? null : phase.id)} className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded-md text-indigo-700 bg-indigo-50 hover:bg-indigo-100 transition-colors" data-id="element-1551">
                          <PackageIcon className="w-3.5 h-3.5 mr-1" data-id="element-1552" />
                          Items ({allocatedItemCount})
                        </button>
                        {deliveryPhases.length > 1 && <button type="button" onClick={() => handleRemovePhase(phase.id)} className="text-gray-400 hover:text-red-600 transition-colors p-1" data-id="element-1553">
                            <TrashIcon className="w-4 h-4" data-id="element-1554" />
                          </button>}
                      </div>
                    </div>
                  </div>

                  {/* Expandable line items allocation */}
                  {isExpanded && lineItems.length > 0 && <div className="px-4 py-3 border-t border-gray-200 bg-white" data-id="element-1555">
                      <p className="text-xs font-medium text-gray-500 uppercase mb-2" data-id="element-1556">
                        Allocate Quantities for Phase {idx + 1}
                      </p>
                      <table className="min-w-full divide-y divide-gray-200 border border-gray-100 rounded-md" data-id="element-1557">
                        <thead className="bg-gray-50" data-id="element-1558">
                          <tr data-id="element-1559">
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1560">
                              Item
                            </th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase w-24" data-id="element-1561">
                              Total Qty
                            </th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase w-28" data-id="element-1562">
                              Other Phases
                            </th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase w-28" data-id="element-1563">
                              Remaining
                            </th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase w-32" data-id="element-1564">
                              This Phase Qty
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100" data-id="element-1565">
                          {lineItems.map((li: any) => {
                      const currentAlloc = phase.lineItemAllocations.find(a => a.itemId === li.id);
                      const currentQty = currentAlloc?.qty || 0;
                      const otherPhasesQty = getAllocatedQty(li.id, phase.id);
                      const remaining = li.qty - otherPhasesQty - currentQty;
                      const maxAllowable = li.qty - otherPhasesQty;
                      return <tr key={li.id} className={currentQty > 0 ? 'bg-indigo-50/50' : ''} data-id="element-1566">
                                <td className="px-3 py-2 text-sm text-gray-900 font-medium" data-id="element-1567">
                                  {li.product}
                                </td>
                                <td className="px-3 py-2 text-sm text-gray-500 text-right" data-id="element-1568">
                                  {li.qty}
                                </td>
                                <td className="px-3 py-2 text-sm text-gray-500 text-right" data-id="element-1569">
                                  {otherPhasesQty}
                                </td>
                                <td className="px-3 py-2 text-right" data-id="element-1570">
                                  <span className={`text-sm font-medium ${remaining < 0 ? 'text-red-600' : remaining === 0 ? 'text-green-600' : 'text-amber-600'}`} data-id="element-1571">
                                    {remaining}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-right" data-id="element-1572">
                                  <input type="number" min="0" max={maxAllowable} className="w-20 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm border p-1.5 text-right" value={currentQty} onChange={e => handleUpdateItemAllocation(phase.id, li.id, Number(e.target.value))} data-id="element-1573" />
                                </td>
                              </tr>;
                    })}
                        </tbody>
                      </table>
                    </div>}
                </div>;
          })}
          </div>

          {/* Payment total validation */}
          <div className="mt-3 flex items-center justify-between px-1" data-id="element-1574">
            <div data-id="element-1575">
              {totalPaymentPercent !== 100 && <p className="text-xs text-red-600 font-medium" data-id="element-1576">
                  ⚠ Total payment percentage must equal 100%. Currently at{' '}
                  {totalPaymentPercent}%.
                </p>}
            </div>
            <p className={`text-sm font-bold ${totalPaymentPercent === 100 ? 'text-green-600' : 'text-red-600'}`} data-id="element-1577">
              Total: {totalPaymentPercent}% = ${totalAmount.toLocaleString()}
            </p>
          </div>
        </div>

        <FormSection title="Additional Details" data-id="element-1578">
          <div className="md:col-span-2" data-id="element-1579">
            <TextAreaField label="Terms & Conditions" name="termsConditions" value={formData.termsConditions} onChange={e => setFormData({
            ...formData,
            termsConditions: e.target.value
          })} rows={3} placeholder="Enter standard or custom PO terms and conditions..." data-id="element-1580" />
          </div>
          <FileUploadField label="Attachments" data-id="element-1581" />
          <div className="md:col-span-2" data-id="element-1582">
            <TextAreaField label="Remarks" name="remarks" value={formData.remarks} onChange={e => setFormData({
            ...formData,
            remarks: e.target.value
          })} rows={2} placeholder="Additional notes for supplier or internal reference..." data-id="element-1583" />
          </div>
        </FormSection>

        <div className="mt-8 pt-5 border-t border-gray-200 flex justify-end space-x-3" data-id="element-1584">
          <button type="button" onClick={() => navigate(-1)} className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-1585">
            Cancel
          </button>
          <button type="button" className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-indigo-700 bg-indigo-50 hover:bg-indigo-100" data-id="element-1586">
            Save as Draft
          </button>
          <button type="button" disabled={!formData.linkedPRNo || !formData.vendor} onClick={() => {
          alert('PO Submitted Successfully!');
          navigate('/purchase-orders');
        }} className={`px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${formData.linkedPRNo && formData.vendor ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-gray-400 cursor-not-allowed'}`} data-id="element-1587">
            {mode === 'create' ? 'Submit PO' : 'Update PO'}
          </button>
        </div>
      </form>
    </div>;
}